import React, { useCallback, useEffect, useState } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { directoryApi } from '../../api/client';
import './ReviewsSection.css';

const AUTO_MS = 5000;

const CLIENT_TYPE_LABELS = {
  particulier: 'Client particulier',
  entreprise: 'Client professionnel',
  organisation: 'Organisation',
};
const CATEGORY_LABELS = { translation: 'Traduction', interpreting: 'Interprétariat' };
const AVATAR_COLORS = ['#1a7a7a', '#c9a227', '#0c1e3c', '#239999', '#152a4a'];
const avatarColor = (name = '') =>
  AVATAR_COLORS[[...name].reduce((acc, ch) => acc + ch.charCodeAt(0), 0) % AVATAR_COLORS.length];

function StarRating({ rating }) {
  return (
    <div className="review-stars" aria-label={`${rating} sur 5`}>
      {Array.from({ length: 5 }, (_, i) => (
        <Star
          key={i}
          size={16}
          className={i < rating ? 'star-filled' : 'star-empty'}
          fill={i < rating ? 'currentColor' : 'none'}
        />
      ))}
    </div>
  );
}

function useVisibleCount() {
  const [count, setCount] = useState(3);

  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      if (w <= 640) setCount(1);
      else if (w <= 992) setCount(2);
      else setCount(3);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  return count;
}

export default function ReviewsSection({ variant = 'page' }) {
  const { t } = useLanguage();
  const visible = useVisibleCount();
  const [testimonials, setTestimonials] = useState(null);
  const [stats, setStats] = useState(null);

  // Témoignages réels uniquement (avis publiés en base).
  // Aucun contenu statique : la section ne s'affiche que s'il existe des avis.
  useEffect(() => {
    let cancelled = false;
    directoryApi.testimonials()
      .then((res) => {
        if (cancelled) return;
        setTestimonials(res.testimonials || []);
        setStats(res.stats || null);
      })
      .catch(() => { if (!cancelled) setTestimonials([]); });
    return () => { cancelled = true; };
  }, []);

  const cards = (testimonials || []).map((rv) => ({
    key: rv.id,
    rating: rv.rating,
    text: rv.comment,
    name: rv.name,
    role: CLIENT_TYPE_LABELS[rv.clientType] || 'Client',
    color: avatarColor(rv.name),
    chips: [...new Set([rv.service || CATEGORY_LABELS[rv.category], rv.document, rv.langs].filter(Boolean))],
  }));

  const total = cards.length;
  const maxIndex = Math.max(0, total - visible);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    setIndex((i) => Math.min(i, maxIndex));
  }, [maxIndex]);

  const goPrev = useCallback(() => {
    setIndex((i) => (i <= 0 ? maxIndex : i - 1));
  }, [maxIndex]);

  const goNext = useCallback(() => {
    setIndex((i) => (i >= maxIndex ? 0 : i + 1));
  }, [maxIndex]);

  useEffect(() => {
    if (paused || maxIndex === 0) return undefined;
    const timer = window.setInterval(goNext, AUTO_MS);
    return () => window.clearInterval(timer);
  }, [paused, maxIndex, goNext]);

  const slidePercent = 100 / visible;
  const isEmbedded = variant === 'embedded';

  // Pendant le chargement initial, on ne rend rien (évite un flash).
  // Ensuite la section s'affiche toujours — avec ou sans avis publiés.
  if (testimonials === null) return null;

  return (
    <section
      className={`section reviews-section${isEmbedded ? ' reviews-section-embedded' : ''}`}
      id={isEmbedded ? undefined : 'avis'}
    >
      <div className={isEmbedded ? undefined : 'container'}>
        <div className="section-header reviews-title-block">
          <h2>{t.reviews.title}</h2>
          {!isEmbedded && <p>{t.reviews.subtitle}</p>}
          {!isEmbedded && stats?.count > 0 && (
            <div className="reviews-score">
              <StarRating rating={Math.round(stats.average)} />
              <strong>
                {stats.average.toFixed(1)}
                <span>/5</span>
              </strong>
              <em>{(t.reviews.basedOnCount || '').replace('{count}', stats.count)}</em>
            </div>
          )}
        </div>

        {cards.length === 0 ? (
          <p style={{ textAlign: 'center', color: 'var(--gray-500)', fontSize: '0.95rem', margin: '0 0 10px' }}>
            Aucun avis pour le moment.
          </p>
        ) : (
        <div
          className="reviews-carousel"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <button
            type="button"
            className="reviews-nav reviews-nav-prev"
            onClick={goPrev}
            aria-label="Previous reviews"
          >
            <ChevronLeft size={22} />
          </button>

          <div className="reviews-viewport">
            <div
              className="reviews-track"
              style={{
                transform: `translateX(-${index * slidePercent}%)`,
              }}
            >
              {cards.map((card) => (
                <article
                  key={card.key}
                  className="review-card"
                  style={{ flex: `0 0 ${slidePercent}%`, alignSelf: 'flex-start' }}
                >
                  <div className="review-card-inner" style={{ gap: '10px', height: 'auto' }}>
                    <Quote size={28} className="review-quote-icon" />
                    <StarRating rating={card.rating} />
                    <p className="review-text" style={{ flex: '0 0 auto' }}>&ldquo;{card.text}&rdquo;</p>
                    <div className="review-footer">
                      <div
                        className="review-avatar"
                        style={{ background: card.color }}
                        aria-hidden="true"
                      >
                        {card.name.charAt(0)}
                      </div>
                      <div className="review-author">
                        <strong>{card.name}</strong>
                        <span>{card.role}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {card.chips.map((chip) => (
                        <span key={chip} className="review-doc">{chip}</span>
                      ))}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>

          <button
            type="button"
            className="reviews-nav reviews-nav-next"
            onClick={goNext}
            aria-label="Next reviews"
          >
            <ChevronRight size={22} />
          </button>
        </div>
        )}

        {cards.length > 0 && (
        <div className="reviews-dots" role="tablist" aria-label="Reviews pagination">
          {Array.from({ length: maxIndex + 1 }, (_, i) => (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={i === index}
              className={`reviews-dot ${i === index ? 'active' : ''}`}
              onClick={() => setIndex(i)}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
        )}
      </div>
    </section>
  );
}
